/**
 * Drizzle schema for Cloudflare D1 (SQLite).
 *
 * Conventions:
 * - All ids are text (uuid v7-ish). The Worker generates them with crypto.randomUUID().
 * - Money columns are stored as INTEGER cents (MXN). Never use REAL for money.
 * - Timestamps are stored as INTEGER unix-seconds.
 * - Boolean columns use INTEGER 0/1.
 */
import { sql } from "drizzle-orm";
import {
  sqliteTable,
  text,
  integer,
  real,
  primaryKey,
  index,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";

const id = () => text("id").primaryKey();
/**
 * Convención: TODA tabla tiene `created_at` (auto al insertar) y `updated_at`
 * (también auto al insertar; la app actualiza manualmente con
 * `Math.floor(Date.now()/1000)` en mutations). Los nombres de columna SQL
 * se hardcodean aquí — no usar nombres alternos como `added_at`,
 * `uploaded_at`, etc. para mantener consistencia.
 */
const createdAt = () =>
  integer("created_at")
    .notNull()
    .default(sql`(unixepoch())`);
const updatedAt = () =>
  integer("updated_at")
    .notNull()
    .default(sql`(unixepoch())`);

// ─── Auth & Roles ────────────────────────────────────────────────────────────

export const users = sqliteTable("users", {
  id: id(),
  email: text("email").notNull().unique(),
  name: text("name").notNull(),
  role: text("role", { enum: ["admin", "finanzas", "lead", "member"] })
    .notNull()
    .default("member"),
  /**
   * Puesto / título de trabajo (lo que se muestra al equipo). Texto
   * libre pero el form lo sugiere desde una lista de puestos típicos
   * de consultoría de software para mantener consistencia. NULL =
   * sin definir (la UI cae a mostrar el role).
   */
  position: text("position"),
  mattermostUserId: text("mattermost_user_id"),
  planeUserId: text("plane_user_id"),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  // Nómina: campos para acoplar el user a pagos de asimilados a salarios.
  // is_payroll_worker controla si el user aparece en el selector de
  // asimilados. RFC/CURP son snapshots fiscales que se pre-rellenan al
  // capturar una tx asimilada. El bruto NO se guarda — varía cada
  // pago (días no trabajados, bonos, ajustes), se captura en su momento.
  rfc: text("rfc"),
  curp: text("curp"),
  isPayrollWorker: integer("is_payroll_worker", { mode: "boolean" }).notNull().default(false),
  /**
   * Tipo de trabajador para finanzas. Determina cómo se calcula IVA/ISR
   * al registrar la tx en /finanzas. NULL = asimilado por default
   * (Art. 96 LISR — IVA forzado a 0). Otros valores cambian el flujo:
   *   - 'becario': IVA NO se fuerza a 0 (el operador captura si aplica).
   *   - 'asimilado': explícito; mismo trato que NULL.
   *   - 'honorarios': Art. 100 LISR.
   */
  workerType: text("worker_type", { enum: ["asimilado", "becario", "honorarios"] }),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

/**
 * Sugerencias de puestos típicos de consultoría de software (México).
 * Lista NO exhaustiva — el campo `position` acepta cualquier texto;
 * estos solo aparecen como sugerencias en el form de admin.
 * Ordenados informalmente de mayor a menor seniority + roles de soporte.
 */
export const USER_POSITION_SUGGESTIONS = [
  "Director(a) General",
  "Director(a) de Tecnología",
  "Director(a) de Operaciones",
  "Gerente de Proyectos",
  "Gerente de Cuenta",
  "Líder Técnico",
  "Arquitecto(a) de Software",
  "Product Owner",
  "Scrum Master",
  "Desarrollador(a) Full Stack",
  "Desarrollador(a) Frontend",
  "Desarrollador(a) Backend",
  "Desarrollador(a) Mobile",
  "Ingeniero(a) DevOps",
  "Ingeniero(a) de Datos",
  "QA / Tester",
  "Diseñador(a) UX/UI",
  "Analista de Negocio",
  "Consultor(a) de Software",
  "Becario(a)",
  "Administración / Operaciones",
  "Contabilidad / Finanzas",
  "Cuenta de servicio",
] as const;

// ─── Projects ────────────────────────────────────────────────────────────────

export const projects = sqliteTable(
  "projects",
  {
    id: id(),
    name: text("name").notNull(),
    slug: text("slug").notNull().unique(),
    type: text("type", { enum: ["client", "internal"] }).notNull(),
    status: text("status", {
      enum: ["discovery", "active", "paused", "completed", "archived"],
    })
      .notNull()
      .default("active"),
    health: text("health", { enum: ["green", "yellow", "red", "unknown"] })
      .notNull()
      .default("unknown"),
    healthNote: text("health_note"),
    clientCompanyId: text("client_company_id"),
    mattermostChannelId: text("mattermost_channel_id"),
    planeProjectId: text("plane_project_id"),
    planeWorkspaceSlug: text("plane_workspace_slug"),
    description: text("description"),
    coverR2Key: text("cover_r2_key"),
    avatarR2Key: text("avatar_r2_key"),
    /** PostHog: URL del proyecto en PostHog si el cliente lo tiene
     * habilitado. Visible en el portal de cliente cuando posthogEnabled. */
    posthogProjectUrl: text("posthog_project_url"),
    posthogEnabled: integer("posthog_enabled", { mode: "boolean" })
      .notNull()
      .default(false),
    startedAt: integer("started_at"),
    targetEndAt: integer("target_end_at"),
    closedAt: integer("closed_at"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    typeIdx: index("projects_type_idx").on(t.type),
    statusIdx: index("projects_status_idx").on(t.status),
  }),
);

export const projectLinks = sqliteTable("project_links", {
  id: id(),
  projectId: text("project_id")
    .notNull()
    .references(() => projects.id, { onDelete: "cascade" }),
  kind: text("kind", {
    enum: ["mural", "drive", "plane", "client_site", "notes", "figma", "github", "gitlab", "other"],
  }).notNull(),
  label: text("label").notNull(),
  url: text("url").notNull(),
  // true: el link soporta preview con iframe (Figma, Drive, etc.).
  // false: solo botón de "abrir externo" (GitHub, GitLab, sitios CSP).
  hasPreview: integer("has_preview", { mode: "boolean" }).notNull().default(true),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const projectMembers = sqliteTable(
  "project_members",
  {
    projectId: text("project_id")
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    roleInProject: text("role_in_project", {
      enum: ["lead", "collaborator", "observer"],
    })
      .notNull()
      .default("collaborator"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.projectId, t.userId] }),
  }),
);

export const projectDocuments = sqliteTable("project_documents", {
  id: id(),
  projectId: text("project_id")
    .notNull()
    .references(() => projects.id, { onDelete: "cascade" }),
  r2Key: text("r2_key").notNull(),
  filename: text("filename").notNull(),
  contentType: text("content_type").notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  kind: text("kind", {
    enum: ["propuesta", "contrato", "entregable", "nota", "otro"],
  })
    .notNull()
    .default("otro"),
  /** Internal user que originalmente registró el doc. Cuando lo sube
   * el cliente, queda como un user sistema sentinel; el verdadero
   * uploader externo va en uploadedByClientUserId. */
  uploadedById: text("uploaded_by_id")
    .notNull()
    .references(() => users.id),
  /** Si lo subió el cliente desde el portal. NULL = subido por staff. */
  uploadedByClientUserId: text("uploaded_by_client_user_id"),
  /** Opt-in: false por default. Staff debe activar explícitamente para
   * que el cliente lo vea en su portal. Auto-true cuando lo sube el
   * cliente. */
  clientVisible: integer("client_visible", { mode: "boolean" })
    .notNull()
    .default(false),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Standups & Days off (Fase 2) ────────────────────────────────────────────
// Modelo definitivo (2026-05-08, segunda iteración):
//
// UN standup por usuario por día (UNIQUE en (user_id, date)).
//
// Estructura de 7 preguntas (refleja el formato actual del equipo):
//   1. yesterday          — ¿Qué tareas realizaste ayer?
//   2. yesterday_blockers — ¿Hubo algún obstáculo que te impidiera completar tareas de ayer?
//   3. general_difficulties — ¿Encontraste alguna dificultad técnica o de otro tipo?
//   4. today              — ¿Qué tareas tienes previstas para hoy?
//   5. today_blockers     — ¿Tienes alguna duda o bloqueo para las tareas de hoy?
//   6. tooling_friction   — ¿Hay alguna herramienta o recurso que te dificulta el avance?
//   7. comments           — Comentarios libres
//
// Cross-posting a Mattermost:
//   - Canal global de standups: post completo (las 7 secciones).
//   - Canal del proyecto (mattermost_channel_id en projects): solo la sección
//     que mencionó al ticket o etiquetó al proyecto — NO todo el standup, para
//     no spammear. mentioned_in_section identifica qué fragmento publicar.
//
// Cross-posting a Plane:
//   - Cada standup_ticket recibe un comentario en el issue con la sección
//     relevante.
//
// El usuario puede etiquetar un proyecto sin un ticket específico vía
// standup_projects (e.g. trabajó en research general del proyecto).

// Solo dos preguntas usan items estructurados (mapeo proyecto/ticket por
// tarea). Las demás son texto libre porque son abiertas.
const STANDUP_ITEM_SECTIONS = ["yesterday", "today"] as const;
// Lista completa de secciones para attachments y render.
const STANDUP_SECTIONS = [
  "yesterday",
  "yesterday_blockers",
  "general_difficulties",
  "today",
  "today_blockers",
  "tooling_friction",
  "comments",
] as const;

export const standups = sqliteTable(
  "standups",
  {
    id: id(),
    userId: text("user_id")
      .notNull()
      .references(() => users.id),
    date: text("date").notNull(), // YYYY-MM-DD
    /** Texto libre de las preguntas abiertas. Las dos con items
     * (yesterday, today_blockers) no tienen texto aquí — su contenido
     * vive en `standup_items`. */
    yesterdayBlockers: text("yesterday_blockers"),
    generalDifficulties: text("general_difficulties"),
    todayBlockers: text("today_blockers"),
    toolingFriction: text("tooling_friction"),
    comments: text("comments"),
    /**
     * Usuarios mencionados con @ en cualquier campo free-text del standup.
     * Persiste como JSON array de user IDs. Las menciones por item viven
     * en `standup_item_mentions`.
     */
    mentionedUserIds: text("mentioned_user_ids", { mode: "json" }).$type<string[]>().notNull(),
    postedToMattermostAt: integer("posted_to_mattermost_at"),
    mattermostPostId: text("mattermost_post_id"),
    mattermostChannelId: text("mattermost_channel_id"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    userDateUniq: uniqueIndex("standups_user_date_uniq").on(t.userId, t.date),
  }),
);

/**
 * Items por sección del standup. Cada item es una entrada/tarea discreta
 * con texto libre + (opcional) proyecto del panel + (opcional) **N tickets
 * Plane** (`standup_item_tickets`) + N menciones + N adjuntos.
 *
 * El caso de uso para N tickets por item: cuando se trabajó en varios
 * tickets/pantallas/componentes con la misma descripción y proyecto,
 * evita duplicar la entrada visual.
 */
export const standupItems = sqliteTable(
  "standup_items",
  {
    id: id(),
    standupId: text("standup_id")
      .notNull()
      .references(() => standups.id, { onDelete: "cascade" }),
    section: text("section", { enum: STANDUP_ITEM_SECTIONS }).notNull(),
    position: integer("position").notNull().default(0),
    /** Descripción libre — puede ser null si el item es solo link/imagen. */
    text: text("text"),
    panelProjectId: text("panel_project_id").references(() => projects.id, {
      onDelete: "set null",
    }),
    /** Tracking del cross-post al canal MM del proyecto (si lo tiene). */
    crossPostStatus: text("cross_post_status", {
      enum: ["pending", "posted", "failed", "skipped"],
    })
      .notNull()
      .default("pending"),
    crossPostMmId: text("cross_post_mm_id"),
    crossPostFailureReason: text("cross_post_failure_reason"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    standupIdx: index("standup_items_standup_idx").on(t.standupId),
    sectionIdx: index("standup_items_section_idx").on(t.standupId, t.section, t.position),
  }),
);

/**
 * Tickets de Plane asociados a un item. Snapshot completo del ticket en
 * el momento de captura (título, url, sequence_id, identifier) — si el
 * ticket cambia o se borra en Plane el standup se sigue leyendo.
 */
export const standupItemTickets = sqliteTable(
  "standup_item_tickets",
  {
    id: id(),
    itemId: text("item_id")
      .notNull()
      .references(() => standupItems.id, { onDelete: "cascade" }),
    position: integer("position").notNull().default(0),
    planeProjectId: text("plane_project_id").notNull(),
    planeIssueId: text("plane_issue_id").notNull(),
    issueTitleSnapshot: text("issue_title_snapshot").notNull(),
    issueUrl: text("issue_url").notNull(),
    sequenceId: integer("sequence_id"),
    projectIdentifier: text("project_identifier"),
    /** Tracking del comentario en Plane (si el toggle está activo). */
    planeCommentStatus: text("plane_comment_status", {
      enum: ["pending", "posted", "failed", "skipped"],
    })
      .notNull()
      .default("pending"),
    planeCommentId: text("plane_comment_id"),
    planeCommentFailureReason: text("plane_comment_failure_reason"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    itemIdx: index("standup_item_tickets_item_idx").on(t.itemId),
    itemIssueUniq: uniqueIndex("standup_item_tickets_uniq").on(t.itemId, t.planeIssueId),
  }),
);

/** Menciones de compañeros por item (many-to-many con users). */
export const standupItemMentions = sqliteTable(
  "standup_item_mentions",
  {
    id: id(),
    itemId: text("item_id")
      .notNull()
      .references(() => standupItems.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    itemUserUniq: uniqueIndex("standup_item_mentions_uniq").on(t.itemId, t.userId),
  }),
);

/**
 * Replies del thread del post del standup en Mattermost, sincronizadas
 * de regreso al panel por el cron. Cada reply se identifica por su
 * `mattermost_post_id` (UNIQUE) para upsert idempotente. Replies del bot
 * (file-replies del propio standup) se filtran al ingestar.
 */
export const standupReplies = sqliteTable("standup_replies", {
  id: id(),
  standupId: text("standup_id")
    .notNull()
    .references(() => standups.id, { onDelete: "cascade" }),
  mattermostPostId: text("mattermost_post_id").notNull().unique(),
  mattermostUserId: text("mattermost_user_id").notNull(),
  authorUsername: text("author_username").notNull(),
  authorDisplayName: text("author_display_name"),
  /** Vinculo al user del panel si lo encontramos por mattermost_user_id. */
  userId: text("user_id").references(() => users.id, { onDelete: "set null" }),
  message: text("message").notNull(),
  /** Timestamps de MM (ms-since-epoch). */
  createdAtMm: integer("created_at_mm").notNull(),
  updatedAtMm: integer("updated_at_mm"),
  /** Cuándo el panel ingiere el reply (unix seconds). */
  ingestedAt: integer("ingested_at")
    .notNull()
    .$defaultFn(() => Math.floor(Date.now() / 1000)),
});

/**
 * Adjuntos: viven a nivel de pregunta (`section` poblado, `itemId` null)
 * o a nivel de item (`itemId` poblado, `section` null). Exactamente uno
 * de los dos. El CHECK de la migración asegura el invariante en BD.
 */
export const standupAttachments = sqliteTable(
  "standup_attachments",
  {
    id: id(),
    standupId: text("standup_id")
      .notNull()
      .references(() => standups.id, { onDelete: "cascade" }),
    section: text("section", { enum: STANDUP_SECTIONS }),
    itemId: text("item_id").references(() => standupItems.id, { onDelete: "cascade" }),
    r2Key: text("r2_key").notNull(),
    mimeType: text("mime_type").notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    filename: text("filename").notNull(),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    standupIdx: index("standup_attachments_standup_idx").on(t.standupId),
    itemIdx: index("standup_attachments_item_idx").on(t.itemId),
  }),
);

export const dayOffRequests = sqliteTable("day_off_requests", {
  id: id(),
  userId: text("user_id")
    .notNull()
    .references(() => users.id),
  startDate: text("start_date").notNull(),
  endDate: text("end_date").notNull(),
  kind: text("kind", { enum: ["vacation", "sick", "personal", "unpaid"] }).notNull(),
  status: text("status", { enum: ["pending", "approved", "rejected", "cancelled"] })
    .notNull()
    .default("pending"),
  reason: text("reason"),
  approverId: text("approver_id").references(() => users.id),
  approverComment: text("approver_comment"),
  decidedAt: integer("decided_at"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Project milestones (cache de Plane modules + épicas) ────────────────────
// Fuente de verdad: Plane. El panel cachea para mostrar avance sin pegar a
// Plane en cada render y para dashboard offline.
//
// Modelo de progreso (decisión 2026-05-08):
//   - Épica = issue de Plane que tiene sub-issues (parent issue con hijos).
//   - Avance de la épica = closed_items / total_items (proporcional).
//   - Avance del milestone = avg de avances de sus épicas.
//   - Si milestone no tiene épicas, progress_pct = null (no cuenta).

export const projectMilestones = sqliteTable(
  "project_milestones",
  {
    id: id(),
    projectId: text("project_id")
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    planeModuleId: text("plane_module_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    targetDate: text("target_date"), // YYYY-MM-DD
    startedDate: text("started_date"),
    completedDate: text("completed_date"),
    status: text("status", {
      enum: ["backlog", "planned", "active", "completed", "cancelled"],
    })
      .notNull()
      .default("planned"),
    planeUrl: text("plane_url").notNull(),
    progressPct: integer("progress_pct"), // null si no hay épicas
    epicsCount: integer("epics_count").notNull().default(0),
    lastSyncedAt: integer("last_synced_at"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    projectIdx: index("milestones_project_idx").on(t.projectId),
    moduleUniq: uniqueIndex("milestones_module_uniq").on(t.projectId, t.planeModuleId),
    targetDateIdx: index("milestones_target_idx").on(t.targetDate),
  }),
);

export const milestoneEpics = sqliteTable(
  "milestone_epics",
  {
    id: id(),
    milestoneId: text("milestone_id")
      .notNull()
      .references(() => projectMilestones.id, { onDelete: "cascade" }),
    planeIssueId: text("plane_issue_id").notNull(),
    name: text("name").notNull(),
    state: text("state"),
    totalItems: integer("total_items").notNull().default(0),
    closedItems: integer("closed_items").notNull().default(0),
    progressPct: integer("progress_pct").notNull().default(0),
    planeUrl: text("plane_url").notNull(),
    lastModifiedAt: integer("last_modified_at"),
    lastSyncedAt: integer("last_synced_at"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    milestoneIdx: index("epics_milestone_idx").on(t.milestoneId),
    issueUniq: uniqueIndex("epics_issue_uniq").on(t.milestoneId, t.planeIssueId),
  }),
);

// ─── Evaluaciones v3 — refundación según filosofía Axorial ──────────────────
// 3 conceptos jerárquicos:
//   1. KPIs (kpi_definitions + kpi_values): inputs cuantitativos. Auto/manual.
//   2. Cuestionarios (questionnaires + questionnaire_instances + responses):
//      inputs cualitativos/mixed que responde la gente.
//   3. Evaluaciones (assessments + assessment_inputs + assessment_results +
//      assessment_versions): marco compuesto que combina KPIs y cuestionarios
//      vía scoring_method para devolver un status. Es lo que el usuario ve.
//
// 4 niveles: client_user, organizational, team, project.
// 4 status: excellent | good | improvable | bad (+ unknown).
// Visibility (4 valores) determina quién ve y quién captura.
// Cuestionarios anónimos: is_anonymous=true → respondent_id nullable.
// Versionado: assessments.current_version + tabla assessment_versions con
// snapshot inmutable de cada versión. assessment_results lleva el version
// con que fue calculado para reconstrucción evolutiva.

const evalLevel = () =>
  text("level", {
    enum: ["client_user", "organizational", "team", "project"],
  }).notNull();

const evalVisibility = () =>
  text("visibility", { enum: ["everyone", "lead", "finanzas", "admin_only"] })
    .notNull()
    .default("everyone");

const evalRecurrence = () =>
  text("recurrence", {
    enum: [
      "monthly",
      "biweekly",
      "quarterly",
      "semester",
      "annual",
      "on_demand",
      "per_project_close",
    ],
  })
    .notNull()
    .default("on_demand");

const appliesToProjects = () =>
  text("applies_to_projects", { enum: ["all", "none", "per_project_opt_in"] })
    .notNull()
    .default("none");

// ─── Cuestionarios (era evaluation_templates) ───────────────────────────────

export const questionnaires = sqliteTable("questionnaires", {
  id: id(),
  name: text("name").notNull(),
  level: evalLevel(),
  visibility: evalVisibility(),
  description: text("description"),
  // Cada item: { id, kind: 'quant'|'qual', prompt, scale?: {min,max,labels}, required }
  itemsJson: text("items_json").notNull(),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  recurrence: evalRecurrence(),
  isAnonymous: integer("is_anonymous", { mode: "boolean" }).notNull().default(false),
  appliesToProjects: appliesToProjects(),
  // nature: 'quantitative' (solo escalas), 'qualitative' (solo texto libre), 'mixed'
  nature: text("nature", { enum: ["quantitative", "qualitative", "mixed"] })
    .notNull()
    .default("mixed"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const questionnaireInstances = sqliteTable("questionnaire_instances", {
  id: id(),
  questionnaireId: text("questionnaire_id")
    .notNull()
    .references(() => questionnaires.id),
  level: evalLevel(),
  projectId: text("project_id").references(() => projects.id),
  period: text("period").notNull(),
  parentInstanceId: text("parent_instance_id"),
  closedAt: integer("closed_at"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const questionnaireResponses = sqliteTable("questionnaire_responses", {
  id: id(),
  questionnaireInstanceId: text("questionnaire_instance_id")
    .notNull()
    .references(() => questionnaireInstances.id, { onDelete: "cascade" }),
  respondentId: text("respondent_id").references(() => users.id),
  answersJson: text("answers_json").notNull(),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── KPIs (era metric_*) ────────────────────────────────────────────────────

export const kpiDefinitions = sqliteTable(
  "kpi_definitions",
  {
    id: id(),
    name: text("name").notNull(),
    description: text("description"),
    level: evalLevel(),
    // Siempre 'quantitative' por ahora — campo explícito para reportes y
    // simetría con questionnaires.nature.
    nature: text("nature", { enum: ["quantitative", "qualitative", "mixed"] })
      .notNull()
      .default("quantitative"),
    unit: text("unit"),
    direction: text("direction", {
      enum: ["higher_better", "lower_better", "range"],
    })
      .notNull()
      .default("higher_better"),
    thresholdExcellent: real("threshold_excellent"),
    thresholdGood: real("threshold_good"),
    thresholdImprovable: real("threshold_improvable"),
    thresholdBad: real("threshold_bad"),
    recurrence: evalRecurrence().default("monthly"),
    active: integer("active", { mode: "boolean" }).notNull().default(true),
    visibility: evalVisibility(),
    appliesToProjects: appliesToProjects(),
    autoSource: text("auto_source"),
    createdBy: text("created_by").references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    levelIdx: index("kpi_definitions_level_idx").on(t.level),
    activeIdx: index("kpi_definitions_active_idx").on(t.active),
  }),
);

export const kpiValues = sqliteTable(
  "kpi_values",
  {
    id: id(),
    definitionId: text("definition_id")
      .notNull()
      .references(() => kpiDefinitions.id, { onDelete: "cascade" }),
    projectId: text("project_id").references(() => projects.id),
    period: text("period").notNull(),
    value: real("value"),
    note: text("note"),
    autoFilled: integer("auto_filled", { mode: "boolean" }).notNull().default(false),
    createdBy: text("created_by").references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    periodIdx: index("kpi_values_period_idx").on(t.period),
  }),
);

// ─── Project activations (generalizada — KPI/cuestionario/assessment) ───────

export const projectActivations = sqliteTable(
  "project_activations",
  {
    id: id(),
    projectId: text("project_id")
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    defId: text("def_id").notNull(),
    defKind: text("def_kind", { enum: ["kpi", "questionnaire", "assessment"] }).notNull(),
    active: integer("active", { mode: "boolean" }).notNull().default(true),
    thresholdExcellentOverride: real("threshold_excellent_override"),
    thresholdGoodOverride: real("threshold_good_override"),
    thresholdImprovableOverride: real("threshold_improvable_override"),
    thresholdBadOverride: real("threshold_bad_override"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    projectIdx: index("project_activations_project_idx").on(t.projectId),
    uniq: uniqueIndex("project_activations_uniq").on(t.projectId, t.defId, t.defKind),
  }),
);

// ─── Assessments (marco compuesto — concepto Evaluación) ────────────────────

export const assessments = sqliteTable(
  "assessments",
  {
    id: id(),
    name: text("name").notNull(),
    description: text("description"),
    level: evalLevel(),
    scoringMethod: text("scoring_method", {
      enum: ["weighted_average", "worst_signal", "custom"],
    })
      .notNull()
      .default("weighted_average"),
    customFormulaJson: text("custom_formula_json"),
    recurrence: evalRecurrence().default("monthly"),
    active: integer("active", { mode: "boolean" }).notNull().default(true),
    visibility: evalVisibility(),
    appliesToProjects: appliesToProjects(),
    currentVersion: integer("current_version").notNull().default(1),
    createdBy: text("created_by").references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    levelIdx: index("assessments_level_idx").on(t.level),
    activeIdx: index("assessments_active_idx").on(t.active),
  }),
);

export const assessmentVersions = sqliteTable(
  "assessment_versions",
  {
    id: id(),
    assessmentId: text("assessment_id")
      .notNull()
      .references(() => assessments.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    snapshotJson: text("snapshot_json").notNull(),
    createdBy: text("created_by").references(() => users.id),
    createdAt: createdAt(),
  },
  (t) => ({
    uniq: uniqueIndex("assessment_versions_uniq").on(t.assessmentId, t.version),
  }),
);

export const assessmentInputs = sqliteTable(
  "assessment_inputs",
  {
    id: id(),
    assessmentId: text("assessment_id")
      .notNull()
      .references(() => assessments.id, { onDelete: "cascade" }),
    inputKind: text("input_kind", { enum: ["kpi", "questionnaire"] }).notNull(),
    inputId: text("input_id").notNull(),
    weight: real("weight").notNull().default(1.0),
    required: integer("required", { mode: "boolean" }).notNull().default(false),
    createdAt: createdAt(),
  },
  (t) => ({
    assessmentIdx: index("assessment_inputs_assessment_idx").on(t.assessmentId),
  }),
);

export const assessmentResults = sqliteTable(
  "assessment_results",
  {
    id: id(),
    assessmentId: text("assessment_id")
      .notNull()
      .references(() => assessments.id, { onDelete: "cascade" }),
    assessmentVersion: integer("assessment_version").notNull(),
    period: text("period").notNull(),
    projectId: text("project_id").references(() => projects.id),
    status: text("status", {
      enum: ["excellent", "good", "improvable", "bad", "unknown"],
    }).notNull(),
    score: real("score"),
    breakdownJson: text("breakdown_json").notNull(),
    computedAt: integer("computed_at")
      .notNull()
      .default(sql`(unixepoch())`),
    notes: text("notes"),
  },
  (t) => ({
    periodIdx: index("assessment_results_period_idx").on(t.period),
    projectIdx: index("assessment_results_project_idx").on(t.projectId),
  }),
);

// ─── CRM ─────────────────────────────────────────────────────────────────────
// Estados:
//   - active   = cliente activo, hay relación comercial vigente
//   - prospect = posible cliente, en proceso de venta
//   - past     = relación cerrada de forma normal (proyecto terminó, ya no hay venta)
//   - lost     = se perdió en proceso de venta sin convertirse en cliente
// La empresa con id="co_axorial_internal" agrupa todos los proyectos internos
// (seed en migrations/0002_seed.sql).

export const companies = sqliteTable("companies", {
  id: id(),
  name: text("name").notNull(),
  industry: text("industry"),
  size: text("size"),
  status: text("status", { enum: ["active", "prospect", "past", "lost"] })
    .notNull()
    .default("prospect"),
  isInternal: integer("is_internal", { mode: "boolean" }).notNull().default(false),
  // Marca empresas que son proveedores (a quienes se les paga por
  // bienes/servicios). Permite agrupar gastos por proveedor sin
  // mezclarse con el CRM de clientes. Una empresa puede ser cliente
  // y proveedor a la vez.
  isSupplier: integer("is_supplier", { mode: "boolean" }).notNull().default(false),
  website: text("website"),
  notes: text("notes"),
  ownerId: text("owner_id").references(() => users.id),
  /** Branding sutil para portal cliente: si la empresa lo tiene
   * habilitado, su logo + accent_color aparecen en el header del
   * portal cliente — el resto del look sigue siendo Axorial. */
  logoR2Key: text("logo_r2_key"),
  accentColor: text("accent_color"),
  clientPortalEnabled: integer("client_portal_enabled", { mode: "boolean" })
    .notNull()
    .default(false),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const contacts = sqliteTable("contacts", {
  id: id(),
  companyId: text("company_id").references(() => companies.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  email: text("email"),
  phone: text("phone"),
  role: text("role"),
  notes: text("notes"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const deals = sqliteTable("deals", {
  id: id(),
  companyId: text("company_id")
    .notNull()
    .references(() => companies.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  stage: text("stage", {
    enum: ["lead", "qualified", "proposal", "negotiation", "won", "lost"],
  })
    .notNull()
    .default("lead"),
  amountMxnCents: integer("amount_mxn_cents").notNull().default(0),
  probability: integer("probability").notNull().default(0), // 0–100
  expectedCloseAt: integer("expected_close_at"),
  ownerId: text("owner_id").references(() => users.id),
  notes: text("notes"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const crmActivities = sqliteTable("crm_activities", {
  id: id(),
  companyId: text("company_id").references(() => companies.id, { onDelete: "cascade" }),
  contactId: text("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  dealId: text("deal_id").references(() => deals.id, { onDelete: "cascade" }),
  kind: text("kind", { enum: ["call", "email", "meeting", "note", "task"] }).notNull(),
  notes: text("notes").notNull(),
  occurredAt: integer("occurred_at").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => users.id),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Finanzas (Fase 4) ───────────────────────────────────────────────────────
// RESICO PM: ISR mensual = 30% × (ingresos COBRADOS - gastos PAGADOS) del mes.
// IVA se separa del subtotal en cada transacción.

export const accounts = sqliteTable("accounts", {
  id: id(),
  name: text("name").notNull(), // ej: "BBVA principal", "Konfio"
  // Tipo de cuenta. credit_card abre creditLimitCents; loan abre los
  // campos loanOriginalCents / loanMonthlyPaymentCents / etc.
  type: text("type", { enum: ["bank", "credit_card", "cash", "loan", "other"] })
    .notNull()
    .default("bank"),
  // Banco emisor (BBVA, Inbursa, Banamex, etc.). Útil cuando el name es genérico.
  bankName: text("bank_name"),
  // Solo aplica para credit_card — la línea autorizada.
  creditLimitCents: integer("credit_limit_cents"),
  currency: text("currency").notNull().default("MXN"),
  openingBalanceCents: integer("opening_balance_cents").notNull().default(0),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  notes: text("notes"),
  // Campos específicos de cuentas tipo 'loan'.
  loanOriginalCents: integer("loan_original_cents"),
  loanMonthlyPaymentCents: integer("loan_monthly_payment_cents"),
  // Tasa anual en basis points: 5800 = 58.00%.
  loanInterestRateBps: integer("loan_interest_rate_bps"),
  loanTotalPayments: integer("loan_total_payments"),
  // Día del mes (1-31) en que se cobra el pago domiciliado.
  loanPaymentDay: integer("loan_payment_day"),
  loanFolio: text("loan_folio"),
  loanNextPaymentDate: text("loan_next_payment_date"),
  // Métodos de pago permitidos al registrar transacciones contra esta
  // cuenta. JSON-array de PaymentMethod (transfer | card_credit |
  // card_debit | cash). El modal filtra el dropdown y, si hay uno solo,
  // lo prellena disabled.
  allowedPaymentMethods: text("allowed_payment_methods", { mode: "json" })
    .$type<Array<"transfer" | "card_credit" | "card_debit" | "cash">>()
    .notNull(),
  // Default sugerido al elegir esta cuenta. Debe estar en
  // allowedPaymentMethods. NULL = sin sugerencia (el modal toma el
  // primero de la lista).
  defaultPaymentMethod: text("default_payment_method", {
    enum: ["transfer", "card_credit", "card_debit", "cash"],
  }),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const finCategories = sqliteTable("fin_categories", {
  id: id(),
  name: text("name").notNull(),
  kind: text("kind", { enum: ["ingreso", "gasto"] }).notNull(),
  // Texto libre para agrupar reportes (ej: "Hosting", "Nóminas", "SaaS").
  // Antes era enum cerrado; lo abrimos para no requerir migration cada vez
  // que aparece un grupo nuevo.
  group: text("group"),
  ivaApplicable: integer("iva_applicable", { mode: "boolean" }).notNull().default(true),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const transactions = sqliteTable(
  "transactions",
  {
    id: id(),
    accountId: text("account_id")
      .notNull()
      .references(() => accounts.id),
    date: text("date").notNull(), // YYYY-MM-DD (devengado/operación)
    kind: text("kind", { enum: ["ingreso", "gasto", "transferencia"] }).notNull(),
    // Cómo se mueve el dinero. Decoupleado del tipo de cuenta porque puedes
    // pagar en efectivo cargando a una caja, o transferir desde una tarjeta.
    paymentMethod: text("payment_method", {
      enum: ["transfer", "card_credit", "card_debit", "cash"],
    }),
    // Si la transacción se repite. Solo metadata para el simulador de
    // proyecciones — el sistema NO genera transacciones automáticas.
    recurrence: text("recurrence", {
      enum: ["none", "weekly", "monthly", "quarterly", "yearly"],
    })
      .notNull()
      .default("none"),
    categoryId: text("category_id").references(() => finCategories.id),
    // Empresa del CRM ligada (cliente para ingresos, proveedor para gastos).
    // Opcional — counterparty queda como texto libre cuando no hay match.
    companyId: text("company_id").references(() => companies.id),
    counterparty: text("counterparty"),
    // Título corto editable por el usuario para identificar la tx en la lista
    // sin depender de companyName/counterparty. Si está vacío, la UI cae a
    // description → counterparty → companyName en ese orden.
    title: text("title"),
    description: text("description"),
    subtotalCents: integer("subtotal_cents").notNull(),
    ivaCents: integer("iva_cents").notNull().default(0),
    totalCents: integer("total_cents").notNull(),
    invoiceStatus: text("invoice_status", {
      // facturado: con CFDI, deducible para ISR + IVA acreditable.
      // deducible_sin_factura: pagos al extranjero / recibos no fiscales con
      //   justificación — deducibles para ISR pero NO suman IVA acreditable.
      // pendiente: aún no se ha facturado pero todavía es posible (cuenta
      //   cuando se cierre).
      // no_aplica: gasto no deducible por naturaleza (personal, multa,
      //   donativo no aplicable).
      // olvidado: era facturable pero pasó la ventana SAT y ya no se puede
      //   emitir CFDI. No deducible — fiscalmente igual a no_aplica, pero
      //   tagueado para reportar pérdida de deducciones por omisión.
      enum: ["facturado", "deducible_sin_factura", "pendiente", "no_aplica", "olvidado"],
    })
      .notNull()
      .default("pendiente"),
    invoiceUuid: text("invoice_uuid"), // UUID del CFDI
    paymentStatus: text("payment_status", {
      enum: ["cobrado", "pagado", "pendiente"],
    })
      .notNull()
      .default("pendiente"),
    paymentDate: text("payment_date"), // YYYY-MM-DD — fecha de flujo
    // Si false, se guarda solo para reportes/historial pero no entra en
    // saldos vivos ni en summary del mes. Útil al cargar transacciones
    // históricas (años pasados) que ya están reflejadas en el saldo inicial.
    affectsTotals: integer("affects_totals", { mode: "boolean" }).notNull().default(true),
    // Si entra al cálculo de ISR e IVA del mes. Default true. Casos donde se
    // desactiva: préstamos recibidos, devoluciones, transferencias mal
    // categorizadas como ingreso. La transacción aún afecta saldos de caja
    // (si affects_totals=true) pero NO suma a la base ISR ni al IVA del SAT.
    isrApplicable: integer("isr_applicable", { mode: "boolean" }).notNull().default(true),
    // El usuario declara que tiene el documento de respaldo fuera del
    // sistema (CFDI en email, recibo físico, otro sistema). Cuando true,
    // la tx no aparece en la lista de "facturas pendientes" del dashboard
    // aunque no haya documento subido aquí. No cambia el efecto fiscal.
    documentedExternally: integer("documented_externally", { mode: "boolean" })
      .notNull()
      .default(false),
    // Marca la tx como asimilado a salarios (Art. 96 LISR). En estos casos
    // total_cents = bruto (deducible), invoice_uuid = CFDI del recibo,
    // isr_retenido_cents = retención a enterar al SAT (informativo, no
    // sale de la cuenta), beneficiary_* = datos del trabajador.
    isAsimilado: integer("is_asimilado", { mode: "boolean" }).notNull().default(false),
    beneficiaryName: text("beneficiary_name"),
    beneficiaryRfc: text("beneficiary_rfc"),
    beneficiaryCurp: text("beneficiary_curp"),
    isrRetenidoCents: integer("isr_retenido_cents"),
    // FK opcional al user de Axorial que recibe el pago. Cuando se
    // selecciona, beneficiary_* heredan snapshot del momento; el FK
    // permite agrupar movimientos por trabajador en reportes.
    payrollUserId: text("payroll_user_id").references(() => users.id),
    // Marca la tx como pago al SAT (enteros mensuales, retenciones, DPA).
    // El padre conserva el total bruto; el desglose IVA/ISR/otros vive en
    // splits con las categorías "Impuestos — *". La línea de captura del
    // pago se guarda aquí para referencia rápida en reportes.
    isSatPayment: integer("is_sat_payment", { mode: "boolean" }).notNull().default(false),
    satLineaCaptura: text("sat_linea_captura"),
    // Periodo declarado al que corresponde el pago (YYYY-MM). Distinto al
    // date de la tx: la declaración del periodo X se paga el día 17 del
    // mes X+1. Sirve para conciliar contra tax_filings y agrupar pagos
    // del mismo periodo (puede haber ISR + IVA + DPA en tx separadas).
    satPeriod: text("sat_period"),
    // Marca el espejo de un transfer interno detectado al importar. Si la
    // misma transferencia aparece en dos estados (cargo en BBVA + abono en
    // Inbursa), una se queda en transactions y la otra se marca aquí para
    // no contar 2x. Solo informativo — los cálculos ya excluyen via JOIN.
    isInternalMirror: integer("is_internal_mirror", { mode: "boolean" }).notNull().default(false),
    // Identifica el batch de importación masiva (UUID). Permite undo y
    // reportes "qué se importó en este batch".
    importBatchId: text("import_batch_id"),
    // FK al archivero financiero. Cuando se sube una factura desde el modal de
    // transacción, se crea el fin_document y se liga aquí. El doc aparece en
    // /finance/archivero por su period + kind sin extra plumbing.
    finDocumentId: text("fin_document_id"),
    projectId: text("project_id").references(() => projects.id),
    attachmentR2Key: text("attachment_r2_key"),
    notes: text("notes"),
    createdById: text("created_by_id")
      .notNull()
      .references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    accountDateIdx: index("tx_account_date_idx").on(t.accountId, t.date),
    paymentDateIdx: index("tx_payment_date_idx").on(t.paymentDate),
    projectIdx: index("tx_project_idx").on(t.projectId),
    asimiladoIdx: index("tx_asimilado_idx").on(t.isAsimilado),
  }),
);

/**
 * Sub-ingresos / sub-gastos: una transacción del banco se reparte
 * fiscalmente en N splits con su propia categoría / proyecto / contraparte.
 * El padre sigue siendo el flujo canónico (cuenta, fecha, payment_status,
 * cliente/proveedor principal). Si no hay splits, todo el monto cuenta al
 * padre. Si hay splits, sum(splits.total) == padre.total (validado en API).
 */
export const transactionSplits = sqliteTable(
  "transaction_splits",
  {
    id: id(),
    transactionId: text("transaction_id")
      .notNull()
      .references(() => transactions.id, { onDelete: "cascade" }),
    categoryId: text("category_id").references(() => finCategories.id),
    projectId: text("project_id").references(() => projects.id),
    companyId: text("company_id").references(() => companies.id),
    description: text("description"),
    subtotalCents: integer("subtotal_cents").notNull(),
    ivaCents: integer("iva_cents").notNull().default(0),
    totalCents: integer("total_cents").notNull(),
    notes: text("notes"),
    position: integer("position").notNull().default(0),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    txIdx: index("tx_splits_tx_idx").on(t.transactionId),
    catIdx: index("tx_splits_category_idx").on(t.categoryId),
    projIdx: index("tx_splits_project_idx").on(t.projectId),
    coIdx: index("tx_splits_company_idx").on(t.companyId),
  }),
);

/**
 * Múltiples archivos por transacción. Caso típico: CFDI = PDF + XML.
 * El "principal" sigue siendo transactions.fin_document_id (para queries
 * legacy y filtros hasAttachment), pero el resto vive en este join.
 */
export const transactionDocuments = sqliteTable(
  "transaction_documents",
  {
    transactionId: text("transaction_id")
      .notNull()
      .references(() => transactions.id, { onDelete: "cascade" }),
    finDocumentId: text("fin_document_id").notNull(),
    attachedAt: integer("attached_at")
      .notNull()
      .default(sql`(unixepoch())`),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.transactionId, t.finDocumentId] }),
    docIdx: index("tx_docs_doc_idx").on(t.finDocumentId),
  }),
);

export const transfers = sqliteTable("transfers", {
  id: id(),
  fromAccountId: text("from_account_id")
    .notNull()
    .references(() => accounts.id),
  toAccountId: text("to_account_id")
    .notNull()
    .references(() => accounts.id),
  amountCents: integer("amount_cents").notNull(),
  date: text("date").notNull(),
  // Mismo concepto que transactions.affects_totals: histórico → false.
  affectsTotals: integer("affects_totals", { mode: "boolean" }).notNull().default(true),
  notes: text("notes"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// Snapshots de saldo por cuenta al final de un periodo. Permite consolidar
// históricos: en vez de re-leer todas las transacciones del 2024, leemos el
// snapshot de "diciembre 2024" para BBVA y arrancamos desde ahí. Las
// transacciones detalladas pueden seguir vivas para reportes.
export const accountSnapshots = sqliteTable(
  "account_snapshots",
  {
    id: id(),
    accountId: text("account_id")
      .notNull()
      .references(() => accounts.id),
    period: text("period").notNull(), // YYYY-MM del cierre
    // Para banco/cash: balance al cierre. Para credit_card: deuda al cierre.
    closingBalanceCents: integer("closing_balance_cents").notNull(),
    inflowCents: integer("inflow_cents").notNull().default(0),
    outflowCents: integer("outflow_cents").notNull().default(0),
    transactionCount: integer("transaction_count").notNull().default(0),
    notes: text("notes"),
    createdById: text("created_by_id")
      .notNull()
      .references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    accountPeriodIdx: index("snapshots_account_period_idx").on(t.accountId, t.period),
  }),
);

// Plantillas para importar movimientos desde CSV/XLSX. Guarda el mapping
// columnas → campos de transacción para poder reusar el mismo formato (ej.
// "BBVA Empresarial" siempre tiene "Fecha", "Concepto", "Cargo", "Abono" en
// las mismas columnas).
export const importTemplates = sqliteTable("import_templates", {
  id: id(),
  name: text("name").notNull(), // ej: "BBVA Empresarial"
  accountId: text("account_id").references(() => accounts.id), // cuenta default opcional
  // JSON serialized: { date: "Fecha", description: "Concepto", amount?: "Monto",
  //   cargoCol?: "Cargo", abonoCol?: "Abono", dateFormat?: "DD/MM/YYYY" }
  mapping: text("mapping").notNull(),
  notes: text("notes"),
  createdById: text("created_by_id")
    .notNull()
    .references(() => users.id),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

/**
 * Documentos del archivero. Soporta tres modos de atadura, no
 * mutuamente excluyentes:
 *   - Periodo (YYYY-MM): edo de cuenta, declaración SAT, factura del mes
 *   - Empresa (CRM): contrato, propuesta, NDA con un cliente/proveedor
 *   - Transacción: vía join `transaction_documents` (CFDI ligado a tx)
 * Un doc sin atadura es "general" — escritura, RFC, ID oficial, etc.
 *
 * `kind` sin CHECK en DB — validación en Zod. Permite crecer el set
 * sin tocar el schema.
 */
export const finDocuments = sqliteTable("fin_documents", {
  id: id(),
  period: text("period"), // YYYY-MM, opcional
  kind: text("kind").notNull(),
  accountId: text("account_id").references(() => accounts.id),
  companyId: text("company_id").references(() => companies.id),
  r2Key: text("r2_key").notNull(),
  filename: text("filename").notNull(),
  contentType: text("content_type").notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  uploadedById: text("uploaded_by_id")
    .notNull()
    .references(() => users.id),
  notes: text("notes"),
  /** Opt-in: false por default. Staff marca cada CFDI/factura que
   * decide exponer al portal de clientes. */
  clientVisible: integer("client_visible", { mode: "boolean" })
    .notNull()
    .default(false),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Declaraciones de impuestos (snapshot vs pagado) ────────────────────────

/**
 * Una declaración por (período, tipo). Sirve para comparar lo que el
 * sistema calculó al momento de presentar (snapshot) con lo que
 * realmente se pagó al SAT, y dejar la línea de captura como referencia.
 *
 * El paid_cents se llena cuando la empresa hace el pago; mientras el row
 * existe pero paid_at es null, el filing está pendiente.
 */
export const taxFilings = sqliteTable("tax_filings", {
  id: id(),
  period: text("period").notNull(), // YYYY-MM
  taxType: text("tax_type", {
    enum: ["isr_propio", "isr_asimilados", "isr_salarios", "iva", "otro"],
  }).notNull(),
  systemCalcCents: integer("system_calc_cents").notNull().default(0),
  paidCents: integer("paid_cents").notNull().default(0),
  paidAt: text("paid_at"), // YYYY-MM-DD
  lineaCaptura: text("linea_captura"),
  transactionId: text("transaction_id").references(() => transactions.id, {
    onDelete: "set null",
  }),
  notes: text("notes"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Auditoría ───────────────────────────────────────────────────────────────

export const auditLog = sqliteTable("audit_log", {
  id: id(),
  actorId: text("actor_id").references(() => users.id),
  action: text("action").notNull(),
  entity: text("entity").notNull(),
  entityId: text("entity_id"),
  diffJson: text("diff_json"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

/**
 * Eventos del calendario — base genérica para feriados, cierres,
 * llamadas, juntas, recordatorios personales y eventos sincronizados
 * (Google Calendar). El flag `blocksWork` controla si el evento
 * descuenta días hábiles (feriados y cierres sí, llamadas no).
 *
 * Visibilidad: si `userId` es null el evento es global (todos lo ven),
 * con userId es personal del usuario.
 */
export const CALENDAR_EVENT_KINDS = [
  "holiday",
  "company_closure",
  "meeting",
  "call",
  "reminder",
  "external",
] as const;

export const CALENDAR_RECURRENCES = ["daily", "weekly", "biweekly", "monthly", "yearly"] as const;

export const GOOGLE_EVENT_VISIBILITIES = ["default", "public", "private", "confidential"] as const;

export const GOOGLE_EVENT_TRANSPARENCIES = ["opaque", "transparent"] as const;

export const GOOGLE_SYNC_STATUSES = [
  "ok",
  "expired_token",
  "auth_error",
  "rate_limited",
  "error",
] as const;

export const calendarEvents = sqliteTable(
  "calendar_events",
  {
    id: id(),
    startDate: text("start_date").notNull(),
    endDate: text("end_date").notNull(),
    /**
     * Hora local del evento. HH:MM (24h). NULL = all-day. Para eventos
     * importados de Google: si el item viene con `date` (sin time) son
     * NULL; si viene con `dateTime` se splitea a date + time.
     */
    startTime: text("start_time"),
    endTime: text("end_time"),
    kind: text("kind", { enum: CALENDAR_EVENT_KINDS }).notNull(),
    blocksWork: integer("blocks_work", { mode: "boolean" }).notNull().default(false),
    source: text("source", { enum: ["manual", "google_calendar"] })
      .notNull()
      .default("manual"),
    externalId: text("external_id"),
    externalUrl: text("external_url"),
    /** ETag del evento en Google. Permite detección de cambios. */
    externalEtag: text("external_etag"),
    /**
     * Visibility de Google. Determina cómo se muestra a otros usuarios:
     *   - public / default → título y notas visibles.
     *   - private / confidential → solo "Ocupado" para todos menos el owner/admin.
     */
    externalVisibility: text("external_visibility", { enum: GOOGLE_EVENT_VISIBILITIES }),
    /**
     * Transparency: opaque = bloquea el día/horario, transparent = no
     * cuenta como tiempo ocupado.
     */
    externalTransparency: text("external_transparency", {
      enum: GOOGLE_EVENT_TRANSPARENCIES,
    }),
    externalHtmlLink: text("external_html_link"),
    /** Link directo a la conferencia (Google Meet típicamente). NULL si
     * el evento no tiene videoconferencia asociada. */
    externalMeetUrl: text("external_meet_url"),
    /** Lista de invitados serializada como JSON. Para detalle/UX del owner
     * del evento; nunca se expone a otros usuarios. */
    externalAttendees: text("external_attendees", { mode: "json" }).$type<
      Array<{
        email: string;
        name?: string;
        response: "accepted" | "declined" | "tentative" | "needsAction";
        organizer?: boolean;
        self?: boolean;
        optional?: boolean;
      }>
    >(),
    /** Para instancias sincronizadas de un evento recurrente de Google,
     * el ID del evento "master". NULL para eventos no recurrentes y para
     * eventos manuales del panel. Habilita el filtro at-read que evita
     * duplicación cuando un master ya está anclado al panel como
     * `kind=meeting` + `recurrence`. */
    externalRecurringEventId: text("external_recurring_event_id"),
    /** iCalUID (RFC 5545). ID canónico que Google preserva idéntico en
     * todos los calendarios donde aparece el evento y en todas las
     * instancias de un recurrente. Permite deduplicar masters anclados
     * vs. instancias sincronizadas cuando el `event.id` difiere entre
     * organizer y attendees (caso típico de juntas con invitados). */
    externalIcalUid: text("external_ical_uid"),
    externalLastSyncedAt: integer("external_last_synced_at"),
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    notes: text("notes"),
    /** Patrón de recurrencia. NULL = una sola vez. */
    recurrence: text("recurrence", { enum: CALENDAR_RECURRENCES }),
    /**
     * Filtro/selección de días de la semana para daily/weekly/biweekly.
     * JSON array de ints 0-6 (0=lunes ... 6=domingo). NULL para
     * monthly/yearly o cuando no aplica.
     */
    recurrenceDays: text("recurrence_days", { mode: "json" }).$type<number[]>(),
    /** Fecha límite (inclusive) de la última ocurrencia. NULL = sin tope. */
    recurrenceUntil: text("recurrence_until"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    rangeIdx: index("calendar_events_range_idx").on(t.startDate, t.endDate),
    userIdx: index("calendar_events_user_idx").on(t.userId),
    kindIdx: index("calendar_events_kind_idx").on(t.kind),
    blocksIdx: index("calendar_events_blocks_idx").on(t.blocksWork),
  }),
);

// ─── Event sessions (registro histórico de juntas recurrentes) ──────────────

export const EVENT_SESSION_STATUSES = ["planned", "completed", "cancelled", "skipped"] as const;

/**
 * Sesiones por ocurrencia de un evento recurrente. Cada ocurrencia tiene
 * su descripción + link Mural + grabación + attachments para guardar
 * registro histórico ("qué pasó en cada junta del lunes").
 */
export const eventSessions = sqliteTable(
  "event_sessions",
  {
    id: id(),
    calendarEventId: text("calendar_event_id")
      .notNull()
      .references(() => calendarEvents.id, { onDelete: "cascade" }),
    occurrenceDate: text("occurrence_date").notNull(),
    description: text("description"),
    muralUrl: text("mural_url"),
    /** URL del iframe embed de Mural — formato `/embed/{uuid}`. Es un ID
     * distinto al share URL (`muralUrl`) y no derivable de él, por lo que
     * el usuario lo pega aparte (puede pegar el `<iframe>` completo y el
     * front extrae el `src=`). El share URL es para "abrir en pestaña". */
    muralEmbedUrl: text("mural_embed_url"),
    recordingUrl: text("recording_url"),
    /** Doc de transcripción auto-generado por Google Meet. */
    transcriptUrl: text("transcript_url"),
    /** Doc de notas auto-generado por Gemini en la junta. */
    geminiNotesUrl: text("gemini_notes_url"),
    /** Última vez que se intentó auto-fetch de artefactos (unix seconds).
     * Habilita back-off: las grabaciones tardan 5-30 min en procesarse y
     * Gemini docs pueden tardar más; no insistimos infinitamente. */
    artifactsLastFetchedAt: integer("artifacts_last_fetched_at"),
    status: text("status", { enum: EVENT_SESSION_STATUSES }).notNull().default("planned"),
    pushedToGoogleAt: integer("pushed_to_google_at"),
    lastEditedById: text("last_edited_by_id").references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    uniq: uniqueIndex("event_sessions_uniq").on(t.calendarEventId, t.occurrenceDate),
    eventIdx: index("event_sessions_event_idx").on(t.calendarEventId),
    dateIdx: index("event_sessions_date_idx").on(t.occurrenceDate),
  }),
);

export const eventSessionAttachments = sqliteTable(
  "event_session_attachments",
  {
    id: id(),
    sessionId: text("session_id")
      .notNull()
      .references(() => eventSessions.id, { onDelete: "cascade" }),
    r2Key: text("r2_key").notNull(),
    filename: text("filename").notNull(),
    mimeType: text("mime_type").notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    uploadedById: text("uploaded_by_id").references(() => users.id),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    sessionIdx: index("event_session_attachments_session_idx").on(t.sessionId),
  }),
);

/**
 * Estado del sync de Google Calendar por usuario. Una fila por usuario
 * que tiene sync habilitado (lazy-creada en el primer enable o en el
 * primer tick del cron).
 */
export const googleCalendarSyncState = sqliteTable("google_calendar_sync_state", {
  userId: text("user_id")
    .primaryKey()
    .references(() => users.id, { onDelete: "cascade" }),
  enabled: integer("enabled", { mode: "boolean" }).notNull().default(true),
  calendarId: text("calendar_id").notNull().default("primary"),
  syncToken: text("sync_token"),
  lastSyncAt: integer("last_sync_at"),
  lastSyncStatus: text("last_sync_status", { enum: GOOGLE_SYNC_STATUSES }),
  lastSyncError: text("last_sync_error"),
  lastSyncCount: integer("last_sync_count").notNull().default(0),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

// ─── Portal de clientes (clientes.axorial.mx) ───────────────────────────────

/**
 * Usuario externo (cliente) con login propio al portal. Aislado de
 * `users` (que son internos del workspace de Google + CF Access). Password
 * hash + salt vía PBKDF2-SHA-256; sesión via cookie HMAC fuera de DB.
 */
export const CLIENT_ROLES = ["admin", "member"] as const;

/**
 * Permisos granulares del portal cliente. Cada uno habilita una
 * pestaña / acción independiente. `projects.view` NO está acá — es
 * implícito (todos los clientes ven sus proyectos por default;
 * para revocar el acceso completo, se desactiva al usuario).
 */
export const CLIENT_PERMISSIONS = [
  "tickets.view",
  "tickets.create",
  "tickets.comment",
  "invoices.view",
  "archives.view",
  "archives.upload",
  "uptime.view",
] as const;

export const clientUsers = sqliteTable("client_users", {
  id: id(),
  email: text("email").notNull().unique(),
  name: text("name").notNull(),
  passwordHash: text("password_hash").notNull(),
  passwordSalt: text("password_salt").notNull(),
  role: text("role", { enum: CLIENT_ROLES }).notNull().default("member"),
  /** JSON array de strings (de CLIENT_PERMISSIONS). */
  permissions: text("permissions", { mode: "json" })
    .$type<string[]>()
    .notNull()
    .default([
      "tickets.view",
      "tickets.create",
      "tickets.comment",
      "invoices.view",
      "archives.view",
      "archives.upload",
      "uptime.view",
    ]),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  lastLoginAt: integer("last_login_at"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

/**
 * Many-to-many user↔company. Permite que una agencia tenga un único
 * client_user con acceso a varias companies. role_in_company habilita
 * admin del lado del cliente (puede editar org chart, invitar más
 * miembros si lo permitimos en v2).
 */
export const clientUserCompanies = sqliteTable(
  "client_user_companies",
  {
    clientUserId: text("client_user_id")
      .notNull()
      .references(() => clientUsers.id, { onDelete: "cascade" }),
    companyId: text("company_id")
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    roleInCompany: text("role_in_company", { enum: CLIENT_ROLES })
      .notNull()
      .default("member"),
    createdAt: createdAt(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.clientUserId, t.companyId] }),
  }),
);

/**
 * Token de invitación. El admin (interno) lo genera desde panel; el
 * `tokenHash` es SHA-256 del raw que se le muestra al admin una sola
 * vez para que lo comparta manualmente con el cliente (WhatsApp, etc.).
 * Al aceptar se crea el client_user y se setea `accepted_at`.
 */
export const clientInvites = sqliteTable("client_invites", {
  id: id(),
  email: text("email").notNull(),
  companyId: text("company_id").references(() => companies.id, { onDelete: "cascade" }),
  role: text("role", { enum: CLIENT_ROLES }).notNull().default("member"),
  /** Permisos que se aplicarán al client_user cuando acepte el invite. */
  permissions: text("permissions", { mode: "json" })
    .$type<string[]>()
    .notNull()
    .default(["tickets.view", "tickets.create", "tickets.comment"]),
  tokenHash: text("token_hash").notNull().unique(),
  invitedById: text("invited_by_id")
    .notNull()
    .references(() => users.id),
  expiresAt: integer("expires_at").notNull(),
  acceptedAt: integer("accepted_at"),
  createdAt: createdAt(),
});

export const clientPasswordResets = sqliteTable("client_password_resets", {
  id: id(),
  clientUserId: text("client_user_id")
    .notNull()
    .references(() => clientUsers.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull().unique(),
  expiresAt: integer("expires_at").notNull(),
  usedAt: integer("used_at"),
  createdAt: createdAt(),
});

/**
 * Organigrama de la empresa del cliente. NO requiere login — admin del
 * cliente arma la estructura con nombres y puestos para que el equipo
 * Axorial sepa con quién hablar. Si tiene email se puede vincular
 * después a un client_user con login (matching opcional).
 */
export const clientOrgMembers = sqliteTable(
  "client_org_members",
  {
    id: id(),
    companyId: text("company_id")
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    parentId: text("parent_id"),
    name: text("name").notNull(),
    position: text("position"),
    email: text("email"),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => ({
    companyIdx: index("client_org_members_company_idx").on(t.companyId, t.displayOrder),
  }),
);

/**
 * Catálogo configurable de categorías de proyecto (web, ecommerce,
 * cloud, mobile, backend, frontend, database, otro). Editable desde
 * admin. Many-to-many con proyectos vía project_category_assignments.
 */
export const projectCategories = sqliteTable("project_categories", {
  id: id(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  color: text("color"),
  displayOrder: integer("display_order").notNull().default(0),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const projectCategoryAssignments = sqliteTable(
  "project_category_assignments",
  {
    projectId: text("project_id")
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    categoryId: text("category_id")
      .notNull()
      .references(() => projectCategories.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.projectId, t.categoryId] }),
  }),
);

/**
 * Audit log de acciones del cliente en el portal. Separado de
 * `audit_log` interno para que no se mezclen los streams.
 */
export const clientActivityLog = sqliteTable(
  "client_activity_log",
  {
    id: id(),
    clientUserId: text("client_user_id").references(() => clientUsers.id, {
      onDelete: "set null",
    }),
    action: text("action").notNull(),
    entity: text("entity"),
    entityId: text("entity_id"),
    ip: text("ip"),
    meta: text("meta", { mode: "json" }),
    createdAt: createdAt(),
  },
  (t) => ({
    userIdx: index("client_activity_log_user_idx").on(t.clientUserId, t.createdAt),
  }),
);

// ─── Uptime monitoring (migración 0064) ─────────────────────────────────────

export const UPTIME_STATUSES = ["up", "down", "degraded"] as const;

export const projectMonitors = sqliteTable("project_monitors", {
  projectId: text("project_id")
    .primaryKey()
    .references(() => projects.id, { onDelete: "cascade" }),
  url: text("url").notNull(),
  intervalMinutes: integer("interval_minutes").notNull().default(5),
  enabled: integer("enabled", { mode: "boolean" }).notNull().default(true),
  expectedStatus: integer("expected_status").notNull().default(200),
  lastCheckedAt: integer("last_checked_at"),
  lastStatus: text("last_status", { enum: UPTIME_STATUSES }),
  lastStatusCode: integer("last_status_code"),
  lastLatencyMs: integer("last_latency_ms"),
  lastError: text("last_error"),
  createdAt: createdAt(),
  updatedAt: updatedAt(),
});

export const projectUptimeChecks = sqliteTable(
  "project_uptime_checks",
  {
    id: id(),
    projectId: text("project_id")
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    checkedAt: integer("checked_at").notNull(),
    status: text("status", { enum: UPTIME_STATUSES }).notNull(),
    statusCode: integer("status_code"),
    latencyMs: integer("latency_ms"),
    error: text("error"),
  },
  (t) => ({
    idx: index("project_uptime_checks_idx").on(t.projectId, t.checkedAt),
  }),
);

// ─── Type exports ────────────────────────────────────────────────────────────

export type User = typeof users.$inferSelect;
export type Project = typeof projects.$inferSelect;
export type ProjectLink = typeof projectLinks.$inferSelect;
export type ProjectMember = typeof projectMembers.$inferSelect;
export type ProjectDocument = typeof projectDocuments.$inferSelect;
export type Standup = typeof standups.$inferSelect;
export type StandupItem = typeof standupItems.$inferSelect;
export type StandupItemTicket = typeof standupItemTickets.$inferSelect;
export type StandupItemMention = typeof standupItemMentions.$inferSelect;
export type StandupReply = typeof standupReplies.$inferSelect;
export type StandupAttachment = typeof standupAttachments.$inferSelect;
export type ProjectMilestone = typeof projectMilestones.$inferSelect;
export type MilestoneEpic = typeof milestoneEpics.$inferSelect;
export type StandupSection = (typeof STANDUP_SECTIONS)[number];
export type StandupItemSection = (typeof STANDUP_ITEM_SECTIONS)[number];
export const STANDUP_SECTION_KEYS = STANDUP_SECTIONS;
export const STANDUP_ITEM_SECTION_KEYS = STANDUP_ITEM_SECTIONS;
export type DayOffRequest = typeof dayOffRequests.$inferSelect;
export type CalendarEventRow = typeof calendarEvents.$inferSelect;
export type CalendarEventKind = (typeof CALENDAR_EVENT_KINDS)[number];
export type EventSession = typeof eventSessions.$inferSelect;
export type EventSessionAttachment = typeof eventSessionAttachments.$inferSelect;
export type EventSessionStatus = (typeof EVENT_SESSION_STATUSES)[number];
export type GoogleCalendarSyncState = typeof googleCalendarSyncState.$inferSelect;
export type GoogleEventVisibility = (typeof GOOGLE_EVENT_VISIBILITIES)[number];
export type GoogleEventTransparency = (typeof GOOGLE_EVENT_TRANSPARENCIES)[number];
export type GoogleSyncStatus = (typeof GOOGLE_SYNC_STATUSES)[number];
export type Questionnaire = typeof questionnaires.$inferSelect;
export type QuestionnaireInstance = typeof questionnaireInstances.$inferSelect;
export type QuestionnaireResponse = typeof questionnaireResponses.$inferSelect;
export type KpiDefinition = typeof kpiDefinitions.$inferSelect;
export type KpiValue = typeof kpiValues.$inferSelect;
export type ProjectActivation = typeof projectActivations.$inferSelect;
export type Assessment = typeof assessments.$inferSelect;
export type AssessmentVersion = typeof assessmentVersions.$inferSelect;
export type AssessmentInput = typeof assessmentInputs.$inferSelect;
export type AssessmentResult = typeof assessmentResults.$inferSelect;
export type Company = typeof companies.$inferSelect;
export type Deal = typeof deals.$inferSelect;
export type Account = typeof accounts.$inferSelect;
export type Transaction = typeof transactions.$inferSelect;
export type TaxFiling = typeof taxFilings.$inferSelect;
// Client portal types
export type ClientUser = typeof clientUsers.$inferSelect;
export type ClientUserCompany = typeof clientUserCompanies.$inferSelect;
export type ClientInvite = typeof clientInvites.$inferSelect;
export type ClientPasswordReset = typeof clientPasswordResets.$inferSelect;
export type ClientOrgMember = typeof clientOrgMembers.$inferSelect;
export type ProjectCategory = typeof projectCategories.$inferSelect;
export type ProjectCategoryAssignment = typeof projectCategoryAssignments.$inferSelect;
export type ProjectMonitor = typeof projectMonitors.$inferSelect;
export type ProjectUptimeCheck = typeof projectUptimeChecks.$inferSelect;
export type ClientActivityLog = typeof clientActivityLog.$inferSelect;
export type ClientRole = (typeof CLIENT_ROLES)[number];
export type UptimeStatus = (typeof UPTIME_STATUSES)[number];
